import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Api from '../Auth/Api';
import { Toast, Modal, Form } from 'react-bootstrap';
import { decodeJWT } from '../Components/jwtUtils';
import perguntasEficacia from '../data/avaliacaoEficacia.json';

function QuestionarioEficacia() {
    const { id } = useParams(); // ID do certificado
    const token = JSON.parse(localStorage.getItem("user_token"));
    const [dataPerguntas, setDataPerguntas] = useState([]);
    const [respostas, setRespostas] = useState({});
    const [toast, setToast] = useState({});
    const [showToast, setShowToast] = useState(false);
    const [enviarAvaliacao, setEnviarAvaliacao] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [infoCertificado, setInfoCertificado] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [comentarios, setComentarios] = useState("");
    const [usuarioStatus, setUsuarioStatus] = useState(null);
    const navigate = useNavigate();
    const nivel = decodeJWT(token).nivel;
    
    // Exibir mensagem de toast
    const exibirToast = useCallback((header, body, redirecionar = false, tempo = 3000, type = 'toast-info') => {
        setToast({ header, body, type });
        setShowToast(true);
        
        if (redirecionar) {
            setTimeout(() => {
                navigate('/certificados');
            }, tempo);
        }
    }, [navigate]);
    
    // Verificar status do usuário
    useEffect(() => {
        async function verificarStatusUsuario() {
            try {
                const response = await Api.get('/api/meu-perfil', {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                
                if (response.data && response.data.status) {
                    setUsuarioStatus(response.data.status);
                    
                    if (response.data.status === 'inativo') {
                        exibirToast('Acesso negado', 'Usuários inativos não podem acessar a avaliação de eficácia.', true, 3000, 'toast-error');
                        return;
                    }
                }
            } catch (error) {
                console.error('Erro ao verificar status do usuário:', error);
                exibirToast('Erro', 'Não foi possível verificar o status do usuário.', true, 3000, 'toast-error');
            }
        }
        
        verificarStatusUsuario();
    }, [token, exibirToast]);

    // Verificar se o usuário é um gestor
    useEffect(() => {
        if (nivel !== 'Gestor') {
            exibirToast('Acesso negado', 'Apenas gestores podem acessar a avaliação de eficácia.', true, 3000, 'toast-error');
        }
    }, [nivel, exibirToast]);
    
    // Alternativas para todas as questões
    const alternativasEficacia = useMemo(() => [
        "1 - Insatisfatório", 
        "2 - Regular", 
        "3 - Satisfatório", 
        "4 - Muito Bom", 
        "5 - Excelente"
    ], []);

    // Carregar perguntas do arquivo JSON
    useEffect(() => {
        // Carregar perguntas do arquivo JSON
        setDataPerguntas(perguntasEficacia);
    }, []);

    // Carregar informações do certificado
    useEffect(() => {
        async function fetchCertificado() {
            if (!id) return;
            
            // Verificar se o usuário está inativo antes de prosseguir
            if (usuarioStatus === 'inativo') {
                return;
            }
            
            setCarregando(true);
            try {
                // Primeiro verificar se o usuário é o gestor responsável pelo aluno
                if (nivel === 'Gestor') {
                    const verificaGestor = await Api.get(`api/verifica-gestor-responsavel/${id}`, {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    });
                    
                    if (!verificaGestor.data.isResponsavel) {
                        exibirToast(
                            'Acesso Negado', 
                            'Você não é o gestor responsável por este aluno.', 
                            true,
                            3000,
                            'toast-error'
                        );
                        return;
                    }
                }
                
                const response = await Api.get(`api/info-certificado/${id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                
                if (response.data && response.data.length > 0) {
                    const certificado = response.data[0];
                    setInfoCertificado(certificado);
                    
                    // Verificar se o aluno está inativo
                    if (certificado.statusAluno === 'inativo') {
                        exibirToast(
                            'Avaliação não disponível', 
                            'Não é possível avaliar a eficácia de usuários inativos.', 
                            true,
                            3000,
                            'toast-warning'
                        );
                        return;
                    }
                    
                    // Verificar se o certificado já foi avaliado
                    if (certificado.avaliacao_eficacia === 1) {
                        exibirToast('Avaliação já respondida', 'Este certificado já possui uma avaliação de eficácia respondida.', true, 3000, 'toast-info');
                        return;
                    }

                    // Verificar se o certificado está expirado ou arquivado
                    if (certificado.status && (certificado.status.expirado || certificado.status.arquivado)) {
                        exibirToast(
                            'Certificado não elegível', 
                            'Certificados expirados ou arquivados não podem receber avaliação de eficácia.', 
                            true,
                            3000,
                            'toast-warning'
                        );
                        return;
                    }
                    
                    // Verificar se o certificado tem mais de 90 dias
                    const dataEmissao = new Date(certificado.data);
                    const hoje = new Date();
                    const diffDias = Math.floor((hoje - dataEmissao) / (1000 * 60 * 60 * 24));
                    
                    if (diffDias < 90) {
                        exibirToast(
                            'Avaliação não disponível', 
                            'Este certificado ainda não completou 90 dias para avaliação de eficácia.', 
                            true,
                            3000,
                            'toast-info'
                        );
                    }
                } else {
                    exibirToast('Certificado não encontrado', 'Não foi possível encontrar o certificado solicitado.', true, 3000, 'toast-error');
                }
            } catch (error) {
                console.error(error.response?.data?.message || error.message);
                exibirToast('Erro', 'Não foi possível carregar informações do certificado.', true, 3000, 'toast-error');
            } finally {
                setCarregando(false);
            }
        }
        
        if (id) {
            fetchCertificado();
        }
    }, [id, token, exibirToast, nivel, usuarioStatus]);

    const [indiceAtual, setIndiceAtual] = useState(0);
    const perguntaAtual = dataPerguntas[indiceAtual];

    const proximaPergunta = useCallback(() => {
        if (!perguntaAtual) return;
        
        // Se for uma pergunta de escala, verificar se há resposta
        if (perguntaAtual.tipo === 'escala' && !respostas[perguntaAtual.id]) {
            exibirToast('Ops!', "Você precisa escolher uma resposta", false, 3000, 'toast-warning');
            return;
        }
        
        // Se for uma pergunta de sim/não, verificar se há resposta
        if (perguntaAtual.tipo === 'sim_nao' && respostas[perguntaAtual.id] === undefined) {
            exibirToast('Ops!', "Você precisa escolher uma resposta", false, 3000, 'toast-warning');
            return;
        }
        
        if (indiceAtual < dataPerguntas.length - 1) {
            setIndiceAtual(indiceAtual + 1);
        } else {
            // Se chegou na última pergunta, mostrar modal de comentários
            setShowConfirmationModal(true);
        }
    }, [perguntaAtual, respostas, indiceAtual, dataPerguntas, exibirToast]);

    const perguntaAnterior = useCallback(() => {
        if (indiceAtual > 0) {
            setIndiceAtual(indiceAtual - 1);
        }
    }, [indiceAtual]);

    const handleSelecao = useCallback((perguntaId, alternativaIndex) => {
        setRespostas(prev => ({
            ...prev,
            [perguntaId]: alternativaIndex
        }));
    }, []);
    
    const handleSimNao = useCallback((perguntaId, valor) => {
        setRespostas(prev => ({
            ...prev,
            [perguntaId]: valor
        }));
    }, []);

    const confirmSubmit = useCallback(async () => {
        if (!respostas || Object.keys(respostas).length < dataPerguntas.length) {
            exibirToast('Atenção!', 'Você precisa responder todas as perguntas.', false, 3000, 'toast-warning');
            setShowConfirmationModal(false);
            return;
        }

        setShowConfirmationModal(false);

        const formattedAnswers = {};

        for (let perguntaId in respostas) {
            const perguntaData = dataPerguntas.find(p => p.id.toString() === perguntaId.toString());
            if (perguntaData) {
                if (perguntaData.tipo === 'escala') {
                    formattedAnswers[perguntaId] = {
                        questao: perguntaData.questao,
                        resposta: alternativasEficacia[respostas[perguntaId] - 1],
                        alternativa: respostas[perguntaId].toString()
                    };
                } else if (perguntaData.tipo === 'sim_nao') {
                    formattedAnswers[perguntaId] = {
                        questao: perguntaData.questao,
                        resposta: respostas[perguntaId] ? "Sim" : "Não",
                        alternativa: respostas[perguntaId] ? "1" : "0"
                    };
                }
            }
        }

        const data = {
            respostas: formattedAnswers,
            idCertificado: id,
            comentarios: comentarios
        }

        try {
            const response = await Api.post(`/api/progresso-avaliacao-eficacia/${id}`, data, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            exibirToast('Concluído!', response.data.message || "Avaliação enviada com sucesso!", false, 3000, 'toast-success');
            setTimeout(() => {
                setEnviarAvaliacao(true);
            }, 1000);
        } catch (error) {
            exibirToast('Ops!', error.response?.data?.message || "Erro ao enviar avaliação", false, 3000, 'toast-error');
            console.error("Erro ao enviar resposta:", error.response?.data?.message || error.message);
        }
    }, [respostas, dataPerguntas, alternativasEficacia, id, token, exibirToast, comentarios]);

    // Componente de carregamento
    const LoadingScreen = () => (
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '50vh' }}>
            <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Carregando...</span>
            </div>
        </div>
    );

    if (carregando || usuarioStatus === null) {
        return <LoadingScreen />;
    }

    // Se o usuário estiver inativo, não renderizar nada (já foi redirecionado)
    if (usuarioStatus === 'inativo') {
        return null;
    }

    return (
        <>
            <Toast
                show={showToast}
                onClose={() => setShowToast(false)}
                delay={4000}
                autohide
                className={`position-fixed top-0 end-0 m-3 toast-icon ${toast.type || 'toast-error'}`}
                style={{ zIndex: 1060 }}
            >
                <Toast.Header>
                    <strong className="me-auto">{toast.header || ''}</strong>
                </Toast.Header>
                <Toast.Body>{toast.body || ''}</Toast.Body>
            </Toast>

            <Modal show={showConfirmationModal} onHide={() => setShowConfirmationModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Finalizar Avaliação</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Comentários e sugestões:</p>
                    <p className="text-muted small">(Cite exemplos de comportamentos observados que indicam aplicação do conteúdo do treinamento ou sugestões de melhoria, percebeu alguma dificuldade de aplicação do conteúdo aprendido?)</p>
                    
                    <Form.Group className="mb-3">
                        <Form.Control 
                            as="textarea" 
                            rows={5} 
                            value={comentarios}
                            onChange={(e) => setComentarios(e.target.value)}
                            placeholder="Digite seus comentários e sugestões aqui..."
                        />
                    </Form.Group>
                    
                    <p>Tem certeza que deseja enviar sua avaliação?</p>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-center bg-light">
                    <button onClick={confirmSubmit} className="btn btn-primary text-white px-5 py-2">
                        Enviar
                    </button>
                    <button className="btn btn-outline-primary text-primary px-5 py-2 ms-4" onClick={() => setShowConfirmationModal(false)} >
                        Revisar Respostas
                    </button>
                </Modal.Footer>
            </Modal>

            <div className={`fade-in w-100`} style={{ marginBottom: '100px' }}>
                <Link onClick={() => navigate(-1)} className="btn text-primary link border-0 fs-6 mt-3 mt-md-0"><i className="bi bi-chevron-left"></i>Voltar</Link>

                {infoCertificado && (
                    <div className="alert alert-info mb-4">
                        <h5 className="alert-heading">Avaliação de Eficácia</h5>
                        <p className="mb-1">Aluno: <strong>{infoCertificado.nomeAluno}</strong></p>
                        <p className="mb-1">Curso: <strong>{infoCertificado.nomeCurso}</strong></p>
                        <p className="mb-0">Certificado emitido em: <strong>{new Date(infoCertificado.data).toLocaleDateString()}</strong></p>
                    </div>
                )}
                
                <div className="mb-4">
                    <div className="card">
                        <div className="card-header bg-primary text-white">
                            <h5 className="mb-0">Escala: utilize a escala abaixo para responder</h5>
                        </div>
                        <div className="card-body p-0">
                            <table className="table table-bordered mb-0">
                                <thead>
                                    <tr>
                                        <th className="text-center">1 = Insatisfatório</th>
                                        <th className="text-center">2 = Regular</th>
                                        <th className="text-center">3 = Satisfatório</th>
                                        <th className="text-center">4 = Muito Bom</th>
                                        <th className="text-center">5 = Excelente</th>
                                    </tr>
                                </thead>
                            </table>
                        </div>
                    </div>
                </div>

                <div className={`${enviarAvaliacao && 'd-none'}`}>
                    <section className="col-12">
                        <div className="card shadow-sm mb-4">
                            <div className="card-header bg-primary text-white py-3">
                                <h4 className="m-0 text-center">Avaliação de Eficácia - Questão {indiceAtual + 1}/{dataPerguntas.length}</h4>
                            </div>
                            <div className="card-body p-4">
                                {perguntaAtual && (
                                    <div>
                                        <p className="fs-5 mb-4 fw-bold">{perguntaAtual.questao}</p>
                                        <hr className='my-4' />
                                        
                                        {perguntaAtual.tipo === 'escala' ? (
                                            <div className="row mb-4">
                                                {[1, 2, 3, 4, 5].map((valor) => (
                                                    <div className="col text-center" key={valor}>
                                                        <div className="form-check d-flex flex-column align-items-center">
                                                            <input
                                                                className="form-check-input mx-auto"
                                                                type="radio"
                                                                name={`pergunta-${perguntaAtual.id}`}
                                                                id={`alternativa-${perguntaAtual.id}-${valor}`}
                                                                value={valor}
                                                                checked={respostas[perguntaAtual.id] === valor}
                                                                onChange={() => handleSelecao(perguntaAtual.id, valor)}
                                                                style={{ width: '1.5em', height: '1.5em' }}
                                                            />
                                                            <label className="form-check-label mt-2 fw-bold" htmlFor={`alternativa-${perguntaAtual.id}-${valor}`}>
                                                                {valor}
                                                            </label>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : perguntaAtual.tipo === 'sim_nao' ? (
                                            <div className="row justify-content-center mb-4">
                                                <div className="col-md-6">
                                                    <div className="row">
                                                        <div className="col text-center">
                                                            <div className="form-check d-flex flex-column align-items-center">
                                                                <input
                                                                    className="form-check-input mx-auto"
                                                                    type="radio"
                                                                    name={`pergunta-${perguntaAtual.id}`}
                                                                    id={`alternativa-${perguntaAtual.id}-sim`}
                                                                    checked={respostas[perguntaAtual.id] === true}
                                                                    onChange={() => handleSimNao(perguntaAtual.id, true)}
                                                                    style={{ width: '1.5em', height: '1.5em' }}
                                                                />
                                                                <label className="form-check-label mt-2 fw-bold" htmlFor={`alternativa-${perguntaAtual.id}-sim`}>
                                                                    Sim
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <div className="col text-center">
                                                            <div className="form-check d-flex flex-column align-items-center">
                                                                <input
                                                                    className="form-check-input mx-auto"
                                                                    type="radio"
                                                                    name={`pergunta-${perguntaAtual.id}`}
                                                                    id={`alternativa-${perguntaAtual.id}-nao`}
                                                                    checked={respostas[perguntaAtual.id] === false}
                                                                    onChange={() => handleSimNao(perguntaAtual.id, false)}
                                                                    style={{ width: '1.5em', height: '1.5em' }}
                                                                />
                                                                <label className="form-check-label mt-2 fw-bold" htmlFor={`alternativa-${perguntaAtual.id}-nao`}>
                                                                    Não
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : null}

                                        <div className="d-flex flex-column flex-md-row justify-content-md-between mt-4 pt-3">
                                            {indiceAtual > 0 ? (
                                                <button onClick={perguntaAnterior} className="btn btn-outline-primary px-4 py-2 mb-2 mb-md-0">
                                                    <i className="bi bi-arrow-left me-2"></i>Questão Anterior
                                                </button>
                                            ) : (
                                                <div></div>
                                            )}
                                            
                                            <button 
                                                onClick={proximaPergunta} 
                                                className={`btn ${indiceAtual === dataPerguntas.length - 1 ? 'btn-success' : 'btn-primary'} text-white px-4 py-2`}
                                            >
                                                {indiceAtual === dataPerguntas.length - 1 ? 'Finalizar' : 'Próxima Questão'}
                                                {indiceAtual !== dataPerguntas.length - 1 && <i className="bi bi-arrow-right ms-2"></i>}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>
                </div>

                <div className={`d-flex justify-content-center align-items-center ${!enviarAvaliacao && 'd-none'}`} style={{ marginTop: '60px' }}>
                    <section className="col-12 col-lg-12 col-xl-8">
                        <div className="d-flex justify-content-center align-items-center bg-primary text-white p-3 rounded-top-4 mt-2">
                            <h4 className="m-0 mt-2">Avaliação de Eficácia concluída!</h4>
                        </div>
                        <div className="d-flex flex-column justify-content-center align-items-center p-4 bg-white rounded-bottom-4 shadow-sm">
                            <div className="success-icon">
                                <i className="bi bi-check-circle-fill" style={{ fontSize: '5rem', color: '#28a745' }}></i>
                            </div>
                            <h3 className="mt-4 text-center">Obrigado por sua avaliação!</h3>
                            <p className="text-center my-3">
                                A avaliação de eficácia foi registrada com sucesso.
                            </p>
                            <div className="d-flex justify-content-center mt-4">
                                <button onClick={() => navigate('/certificados')} className="btn btn-primary text-white px-4 py-2">
                                    Voltar para Certificados
                                </button>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </>
    )
}

export default QuestionarioEficacia; 